<script>
  import { Link } from "svelte-routing";
  import { createEventDispatcher, getContext } from "svelte";
  import { DimText, Dimension, Quantity } from "dimtext";
  import get from "lodash/get";
  import uniqBy from "lodash/uniqBy";
  import cloneDeep from "lodash/cloneDeep";
  import { createBoxRefPlanes, createCrossRefPlanes, createGridLines } from "@local/lamina-core";

  import EllipsisIcon from "@local/assets/icons/ellipsis.svg";
  import RefPlanesBoxIcon from "@local/assets/icons/ref-planes-box.svg";
  import RefPlanesCrossIcon from "@local/assets/icons/ref-planes-cross.svg";
  import CircleXIcon from "@local/assets/icons/circle-x.svg";
  import CopyIcon from "@local/assets/icons/duplicate.svg";
  import ItemViewButtons from "#lib/sidebar/ItemViewButtons.svelte";
  import InfoIcon from "@local/assets/icons/info.svg";

  import SidebarSection from "./SidebarSection.svelte";
  import TextInput from "./TextInput.svelte";
  import FabricationsInput from "./FabricationsInput.svelte";
  import DimtextInput from "./DimtextInput.svelte";
  import DimtextInputMultiple from "./DimtextInputMultiple.svelte";
  import BooleanInput from "./BooleanInput.svelte";
  import DimtextOffsetInput from "./DimtextOffsetInput.svelte";
  import SelectInput from "./SelectInput.svelte";
  import ContenteditableInput from "./ContenteditableInput.svelte";
  import FlagInput from "./FlagInput.svelte";
  import ButtonInput from "./ButtonInput.svelte";
  import Item2dEditor from "#lib/Item2dEditor.svelte";
  import MiniToolbar from "#lib/toolbar/MiniToolbar.svelte";
  import ItemThumbnail from "#lib/sidebar/ItemThumbnail.svelte";
  import LlmRequestInfo from "#lib/sidebar/LlmRequestInfo.svelte";

  import { scaleOptions, definedMultipliers } from "@local/extensions/utilities/scales.js";
  import containedLocations from "#src/extensions/contained-locations.js";
  import parsePosInteger from "@local/extensions/parsers/parse-pos-integer.js";
  import Polyface from "@local/extensions/geometry/polyface.js";
  import { Modal, tooltip } from "svelte-utilities";
  import CustomScaleModal from "#lib/sidebar/CustomScaleModal.svelte";
  import { alerts } from "#src/stores/alerts.js";
  import copyToClipboard from "#src/extensions/copy-to-clipboard.js";
  import { profile } from "#src/stores/auth.js";
  import { UX_MODE } from "#src/env.js";

  export let types;
  export let group;
  export let item;
  export let collections = [];
  export let itemid = null;
  export let settings;
  export let standardColumns = {};
  export let customColumns = [];
  export let isCollection = false;
  export let disabled = false;
  export let multiple = false;
  export let showEditor = false;
  export let showDrawingScale = false;
  // FrameX minimal mode: show only Frame Scale, Mark, Width, Height, Flag and
  // hide everything else. Defaults false, so the full legacy panel is
  // unchanged everywhere it is used outside FrameX.
  export let framex = false;

  const dt = new DimText();
  const dispatch = createEventDispatcher();
  const edgeTreatments = getContext("edgeTreatmentOptions");

  let showIndividualEdges = !treatmentsAreEqual(item);
  let refreshKey = crypto.randomUUID();
  let convertModal;
  let llmRequestModal;
  let customScaleModal;

  const refPlaneButtons = [
    { component: RefPlanesBoxIcon, label: "Box", value: "box", stateful: true },
    { component: RefPlanesCrossIcon, label: "Cross", value: "cross", stateful: true },
  ];

  const gridLineButtons = [{ component: RefPlanesBoxIcon, label: "Grid", value: "grid", stateful: true }];

  $: displayUnit = settings.display_unit;
  $: item && refresh();
  $: locations = multiple ? [] : containedLocations(item, $group).map((locId) => $group.locations[locId]);
  $: itemsNotInCollection = locations.filter((l) => {
    const record = $group.items[l.record_id];
    return record && record.collection_id !== item.id;
  });
  $: type = $group.types[item?.type_id];
  $: areaFormatter = makeAreaFormatter(displayUnit);
  $: mixedShape = item.shape === "Mixed";
  $: typeOptions = getTypeOptions($group, types);
  $: currentType = $group.types[item?.type_id];
  $: disabledProps = getDisabledProps($group, currentType, item);
  $: collectionOptions = [{ label: "None", value: null }].concat(
    collections.map((c) => ({ label: c.mark, value: c.id })),
  );
  $: shapeItem = item.shape.type !== "none";
  $: noShapePropUpdates = item.shape.type === "free" || mixedShape;
  $: noTypeUpdates = $group?.project_type === "product";
  $: polyface = new Polyface(item, $group.data.fabrications);
  $: edgeIndices = mixedShape
    ? item.data.fabrications.edge_treatment.map((t, i) => i)
    : polyface.shape.flat().map((v, i) => i);
  $: treatment = edgeIndices.map((i) => get(item.data, ["fabrications", "edge_treatment", i]) || null);
  $: allTreatments = treatment.reduce((a, t) => {
    if (t === a) return a;
    return "Mixed";
  }, treatment[0]);
  $: updateShowState(itemid);

  function getDisabledProps(grp, type, item) {
    if (!type) return {};
    if (!type.item_template_mask) return {};
    const props = {};

    if (grp.project_type === "product") {
      props.type_id = true;
    }

    return Object.entries(type.item_template_mask).reduce((o, [k, v]) => {
      if (v) o[k] = true;
      return o;
    }, props);
  }

  function getTypeOptions(grp, types) {
    if (grp.project_type === "product") {
      return types.map((t) => ({
        label: t.name,
        value: t.id,
      }));
    } else {
      return types.map((t) => ({
        label: `${t.mark}${t.name && ` - ${t.name}`}`,
        value: t.id,
      }));
    }
  }

  function makeAreaFormatter(displayUnit) {
    return (a) => {
      return displayUnit === "inches"
        ? typeof a === "number"
          ? a.toFixed(2)
          : ""
        : typeof a === "number"
          ? (a * 0.092903).toFixed(2)
          : "";
    };
  }

  function getItemDrawingValue(item) {
    if (!item.replace_with_drawing) return "shape";
    if (item.show_drawing_overlay) return "both";
    return "drawing";
  }

  function updateItemDrawingValue(e) {
    const val = e.detail.value;
    if (val === "shape") {
      dispatch("updateItem", {
        id: item.id,
        diff: { replace_with_drawing: false, show_drawing_overlay: false },
      });
    } else if (val === "drawing") {
      dispatch("updateItem", {
        id: item.id,
        diff: { replace_with_drawing: true, show_drawing_overlay: false },
      });
    } else if (val === "both") {
      dispatch("updateItem", {
        id: item.id,
        diff: { replace_with_drawing: true, show_drawing_overlay: true },
      });
    }
  }

  function refresh() {
    refreshKey = crypto.randomUUID();
  }

  function getClipScale() {
    return item.drawing?.clip?.drawing_scale || null;
  }

  function clipScaleOptions(drawing) {
    if (drawing?.clip?.drawing_scale && !definedMultipliers[drawing.clip.drawing_scale]) {
      return [
        { label: "None", value: null },
        { label: "Set Custom Scale", value: "custom", unclickable: true },
        ...scaleOptions,
        { label: `Custom Scale: ${drawing.clip.drawing_scale}`, value: drawing.clip.drawing_scale },
      ];
    }
    return [
      { label: "None", value: null },
      { label: "Set Custom Scale", value: "custom", unclickable: true },
      ...scaleOptions,
    ];
  }

  function treatmentsAreEqual(item) {
    const s = new Polyface(item, $group.data.fabrications);
    const edges = s.edges.flat();
    const t = edges.map((edge, i) => get(item.data, ["fabrications", "edge_treatment", i]));
    const first = t[0];
    if (first === "Mixed") return false;
    return t.every((t) => t === first);
  }

  function updateShowState(itemid) {
    showIndividualEdges = !treatmentsAreEqual(item);
  }

  function updateItem(prop, value) {
    dispatch("updateItem", { id: item.id, prop, value });
  }

  function confirmToggleItemOpening() {
    convertModal.open();
  }

  function toggleItemOpening() {
    const locationsByRecord = $group.locations.order.reduce((acc, locId) => {
      const loc = $group.locations[locId];
      if (!acc[loc.record_id]) acc[loc.record_id] = [];
      acc[loc.record_id].push(locId);
      return acc;
    }, {});
    const ids = Array.isArray(item.id) ? item.id : [item.id];
    const childLocations = ids.reduce((acc, id) => {
      const locs = locationsByRecord[id] || [];
      acc.push(...locs);
      return acc;
    }, []);
    let updates = [];
    if (item.is_collection) {
      updates = [
        ...ids.map((id) => ({ id, type: "item", prop: "is_collection", value: false })),
        ...childLocations.map((locId) => ({
          id: locId,
          type: "location",
          prop: "record_type",
          value: "item",
        })),
      ];
    } else {
      updates = [
        ...ids.map((id) => ({
          id,
          type: "item",
          diff: { is_collection: true, type_id: null, collection_id: null },
        })),
        ...childLocations.map((locId) => ({
          id: locId,
          type: "location",
          prop: "record_type",
          value: "collection",
        })),
      ];
    }
    group.update(updates);
  }

  function removeDrawing() {
    dispatch("updateItem", { id: item.id, prop: "drawing", value: null });
  }

  function copyImageToClipboard() {
    copyToClipboard({
      type: "item-drawing",
      data: {
        item_id: item.id,
      },
    });
  }

  function addItemsToOpening() {
    if (!itemsNotInCollection.length) return;

    const ids = uniqBy(itemsNotInCollection, (loc) => loc.record_id).map((loc) => loc.record_id);

    group.updateMultipleItems(ids, "collection_id", item.id);
  }

  function addItemsToMultipleOpenings() {
    if (!item.id) return;

    const updates = [];

    item.id.forEach((id) => {
      const i = $group.items[id];
      const contained = containedLocations(i, $group).map((locId) => $group.locations[locId]);
      const notInCollection = contained.filter((l) => {
        const record = $group.items[l.record_id];
        return record && record.collection_id !== id;
      });

      const ids = uniqBy(notInCollection, (loc) => loc.record_id).map((loc) => loc.record_id);

      ids.forEach((itemId) => {
        updates.push({ id: itemId, type: "item", prop: "collection_id", value: id });
      });
    });

    if (updates.length) {
      group.update(updates);
      alerts.add({ type: "ok", message: "Items added to openings." });
    }
  }

  function updateRefPlanes(value) {
    if (!value) {
      dispatch("updateItem", { id: item.id, prop: "data.reference_planes", value: null });
    } else if (value === "box") {
      const ids = Array.isArray(item.id) ? item.id : [item.id];
      const updates = ids.map((id) => {
        const it = $group.items[id];
        const rp = createBoxRefPlanes();
        const bbox = it.cache.bbox;
        let top = bbox.ymax;
        let right = bbox.xmax;
        let bottom = bbox.ymin;
        let left = bbox.xmin;
        if (displayUnit === "millimeters") {
          top = top * 25.4;
          right = right * 25.4;
          bottom = bottom * 25.4;
          left = left * 25.4;
        }

        rp.top.value = new Dimension(new Quantity(top, displayUnit));
        rp.right.value = new Dimension(new Quantity(right, displayUnit));
        rp.bottom.value = new Dimension(new Quantity(bottom, displayUnit));
        rp.left.value = new Dimension(new Quantity(left, displayUnit));

        return { id, type: "item", prop: "data.reference_planes", value: rp };
      });

      group.update(updates);
    } else if (value === "cross") {
      const ids = Array.isArray(item.id) ? item.id : [item.id];
      const updates = ids.map((id) => {
        const it = $group.items[id];
        const rp = createCrossRefPlanes();
        const bbox = it.cache.bbox;
        let ver = bbox.xmin + bbox.width / 2;
        let hor = bbox.ymin + bbox.height / 2;
        if (displayUnit === "millimeters") {
          ver = ver * 25.4;
          hor = hor * 25.4;
        }

        if (Number.isFinite(ver)) {
          rp.vertical.value = new Dimension(new Quantity(ver, displayUnit));
        }
        if (Number.isFinite(hor)) {
          rp.horizontal.value = new Dimension(new Quantity(hor, displayUnit));
        }

        return { id, type: "item", prop: "data.reference_planes", value: rp };
      });

      group.update(updates);
    }
  }

  function updateGridLines(value) {
    if (!value) {
      dispatch("updateItem", { id: item.id, prop: "data.reference_planes", value: null });
    } else if (value === "grid") {
      const ids = Array.isArray(item.id) ? item.id : [item.id];
      const updates = ids
        .map((id) => {
          const it = $group.items[id];
          if (!it.cache.width || !it.cache.height) return null;
          const xSubdivs = item.x_subdivisions || 1;
          const ySubdivs = item.y_subdivisions || 1;

          const gl = createGridLines(it.cache.width, it.cache.height, xSubdivs, ySubdivs);

          return { id, type: "item", prop: "data.reference_planes", value: gl };
        })
        .filter((update) => update);

      group.update(updates);
    }
  }

  function updateSubdivs(prop, value) {
    const ids = Array.isArray(item.id) ? item.id : [item.id];
    const updates = ids.map((id) => {
      const it = $group.items[id];

      const diff = { [prop]: value };

      const rp = it.data.reference_planes;
      if (rp?.type === "grid") {
        let v = it.x_subdivisions;
        let h = it.y_subdivisions;
        if (prop === "x_subdivisions") {
          v = value;
        } else if (prop === "y_subdivisions") {
          h = value;
        }

        if (rp.vertical.length !== v || rp.horizontal.length !== h) {
          const gl = createGridLines(it.cache.width, it.cache.height, v, h);
          const data = cloneDeep(it.data);

          if (rp.vertical.length !== v) {
            data.reference_planes.vertical = gl.vertical;
          }

          if (rp.horizontal.length !== h) {
            data.reference_planes.horizontal = gl.horizontal;
          }

          diff.data = data;
        }
      }

      return { type: "item", id, diff };
    });

    group.update(updates);
  }

  function updatedWidth(item, offset) {
    const pf = new Polyface(item, $group.data.fabrications);
    const bbox = pf.bbox;
    const w = bbox.width - offset.toNumber("inches");
    return dt.parse(w.toString()).value;
  }

  function updatedHeight(item, offset) {
    const pf = new Polyface(item, $group.data.fabrications);
    const bbox = pf.bbox;
    const h = bbox.height - offset.toNumber("inches");
    return dt.parse(h.toString()).value;
  }

  function updateItemWidth(value) {
    const diff = { width: value };

    try {
      if (isCollection && value && item.height != null && !mixedShape) {
        // Add gridlines for newly-defined openings
        if (
          !item.width &&
          !item.data.reference_planes &&
          Number.isFinite(item.x_subdivisions) &&
          Number.isFinite(item.y_subdivisions)
        ) {
          const gl = createGridLines(
            value.toNumber("inches"),
            item.height.toNumber("inches"),
            item.x_subdivisions,
            item.y_subdivisions,
          );
          diff["data.reference_planes"] = gl;

          // Flex gridlines if already present
        } else if (item.width && item.data.reference_planes?.type === "grid") {
          const ov = item.width.toNumber(displayUnit);
          const nv = value.toNumber(displayUnit);
          const d = nv - ov;

          const gl = cloneDeep(item.data.reference_planes);
          const last = gl.vertical[gl.vertical.length - 1];
          if (last) {
            last.value = new Dimension(new Quantity(last.value.toNumber(displayUnit) + d, displayUnit));
            diff["data.reference_planes"] = gl;
          }
        }
      }
    } catch (err) {
      console.error("Unable to update gridlines", err);
    }

    dispatch("updateItem", { id: item.id, diff });
  }

  function updateItemHeight(value) {
    const diff = { height: value };

    try {
      if (isCollection && value && item.width != null && !mixedShape) {
        // Add gridlines for newly-defined openings
        if (
          !item.height &&
          !item.data.reference_planes &&
          Number.isFinite(item.x_subdivisions) &&
          Number.isFinite(item.y_subdivisions)
        ) {
          const gl = createGridLines(
            item.width.toNumber("inches"),
            value.toNumber("inches"),
            item.x_subdivisions,
            item.y_subdivisions,
          );
          diff["data.reference_planes"] = gl;

          // Flex gridlines if already present
        } else if (item.height && item.data.reference_planes?.type === "grid") {
          const ov = item.height.toNumber(displayUnit);
          const nv = value.toNumber(displayUnit);
          const d = nv - ov;

          const gl = cloneDeep(item.data.reference_planes);
          const last = gl.horizontal[gl.horizontal.length - 1];
          if (last) {
            last.value = new Dimension(new Quantity(last.value.toNumber(displayUnit) + d, displayUnit));
            diff["data.reference_planes"] = gl;
          }
        }
      }
    } catch (err) {
      console.error("Unable to update gridlines", err);
    }

    dispatch("updateItem", { id: item.id, diff });
  }

  function updateItemWidthOffset(e) {
    const { field, value } = e.detail;
    if (field === "input") {
      updateItemWidth(value);
    } else {
      if (item.shape.type === "free") {
        const width = updatedWidth(item, value);
        dispatch("updateItem", { id: item.id, diff: { width_offset: value, width } });
      } else {
        dispatch("updateItem", { id: item.id, prop: "width_offset", value });
      }
    }
  }

  function updateItemHeightOffset(e) {
    const { field, value } = e.detail;
    if (field === "input") {
      updateItemHeight(value);
    } else {
      if (item.shape?.type === "free") {
        const height = updatedHeight(item, value);
        dispatch("updateItem", { id: item.id, diff: { height_offset: value, height } });
      } else {
        dispatch("updateItem", { id: item.id, prop: "height_offset", value });
      }
    }
  }

  function updateData(path, value) {
    const prop = `data.${path.join(".")}`;
    dispatch("updateItem", { id: item.id, prop, value });
  }

  function updateAllEdges(value) {
    const treatments = edgeIndices.map((e) => value);
    dispatch("updateItem", {
      id: item.id,
      prop: "data.fabrications.edge_treatment",
      value: treatments,
    });
  }

  function updateCustomColData(id, val) {
    const prop = `custom_column_data.${id}`;
    dispatch("updateItem", { id: item.id, prop, value: val });
  }

  function getPropName(name, prop) {
    return standardColumns[prop]?.override ? standardColumns[prop].override : name;
  }

  function updateClipScale(e) {
    const value = e.detail.value;
    const drawing = cloneDeep(item.drawing);
    if (!drawing?.clip) return;

    drawing.clip.drawing_scale = value;

    const updates = [
      {
        type: "item",
        id: item.id,
        diff: { drawing },
      },
    ];

    const loc = $group.locations[drawing.attachment_id];
    if (loc) {
      updates.push({
        type: "location",
        id: loc.id,
        diff: { clip: drawing.clip },
      });
    }

    group.update(updates);
  }

  function toggleBug() {
    const data = cloneDeep(item.data);
    if (!data.fabrications) {
      data.fabrications = {};
    }

    if (!data.fabrications.bug) {
      data.fabrications.bug = {
        type: "rectangle",
        width: dt.parse("1").value,
        height: dt.parse("1").value,
        alignment: "inside-edge",
        orientation: "orthogonal",
        reference: {
          type: "edge-offset-position",
          loop: 0,
          edge: 0,
          length: dt.parse("3").value,
          offset: dt.parse("3").value,
          start: "start",
        },
      };
    } else {
      delete data.fabrications.bug;
    }

    dispatch("updateItem", { id: item.id, prop: "data", value: data });
  }
</script>

{#if !framex && item.drawing && showDrawingScale}
  {@const clipScale = getClipScale()}
  {@const options = clipScaleOptions(item.drawing)}
  <SidebarSection>
    <SelectInput
      {disabled}
      label="Clip Scale"
      labelWidth="7.5rem"
      value={clipScale}
      {options}
      on:input={updateClipScale}
      on:option-click={() => customScaleModal.open()} />
  </SidebarSection>
{/if}
{#if !framex}
{#if multiple}
  <SidebarSection>
    <div class="relative image-container">
      <div
        class="relative flex items-center justify-center rounded border border-transparent hover:border-gray-300">
        {#if !disabled && item.drawing}
          <ItemViewButtons {item} on:updateItem />
        {/if}
        {#key refreshKey}
          <ItemThumbnail {group} {item} {locations} {isCollection} />
        {/key}
      </div>
    </div>
    <div class="px-2 pt-2 flex justify-end">
      <button class="btn-text" on:click|stopPropagation={addItemsToMultipleOpenings}
        >Add Items to Opening</button>
    </div>
  </SidebarSection>
{:else}
  <div class="thumbnail-image">
    {#if showEditor}
      <div class="border mx-4">
        <MiniToolbar {disabled} />
        <div class="mini-editor">
          <Item2dEditor
            paddingTop={80}
            paddingRight={80}
            paddingBottom={80}
            paddingLeft={80}
            {disabled}
            {isCollection}
            {group}
            {item}
            itemid={item?.id}
            {type}
            {types} />
        </div>
      </div>
      {#if UX_MODE !== "shpx"}
        <SidebarSection>
          <Link to={`items/${item?.id}`}><div class="px-2 mt-2 text-blue-500">View Item</div></Link>
        </SidebarSection>
      {/if}
    {:else}
      <SidebarSection>
        <div class="relative image-container">
          <div
            class="relative flex items-center justify-center rounded border border-transparent hover:border-gray-300">
            {#if !disabled && item.drawing}
              <ItemViewButtons {item} on:updateItem />
            {/if}
            {#key refreshKey}
              <ItemThumbnail {group} {item} {locations} {isCollection} />
            {/key}
          </div>
          {#if item.drawing && item.replace_with_drawing && !disabled}
            <button
              use:tooltip={{ text: "Remove Drawing" }}
              class="image-action-button absolute top-0 right-0 text-gray-500 hover:text-black rounded p-1 hover:bg-gray-100"
              on:click|stopPropagation={removeDrawing}>
              <CircleXIcon />
            </button>
            <button
              use:tooltip={{ text: "Copy to Clipboard" }}
              class="image-action-button absolute bottom-0 right-0 text-gray-500 hover:text-black rounded p-1 hover:bg-gray-100"
              on:click|stopPropagation={copyImageToClipboard}>
              <CopyIcon />
            </button>
          {/if}
        </div>
        {#if itemsNotInCollection.length > 0}
          <div class="px-2 pt-2 flex justify-end">
            <button class="btn-text" on:click|stopPropagation={addItemsToOpening}
              >Add Items to Opening</button>
          </div>
        {/if}
      </SidebarSection>
    {/if}
  </div>
{/if}
{/if}
<SidebarSection>
  {#if !framex}
    <h3 class="px-2 mb-2">Properties</h3>
  {/if}
  <div class="flex gap-2">
    <TextInput
      label={getPropName("Mark", "mark")}
      disabled={disabled || disabledProps.mark}
      labelWidth="5.5rem"
      value={item.mark}
      on:input={(e) => updateItem("mark", e.detail.value)} />
    {#if !multiple && item.llm_request_log_id && $profile?.user_role === "developer"}
      <div class="flex items-center">
        <button
          class="p-1 rounded text-gray-500 hover:bg-gray-200"
          on:click={() => llmRequestModal.open(item.llm_request_log_id)}>
          <InfoIcon />
        </button>
      </div>
    {/if}
  </div>
  {#if shapeItem}
    {#if settings.hide_width_offset}
      <DimtextInput
        label={getPropName("Width", "width")}
        disabled={disabled || noShapePropUpdates || disabledProps.width}
        labelWidth="5.5rem"
        value={item.width}
        {settings}
        allowNull
        on:input={(e) => updateItemWidth(e.detail.value)} />
    {:else}
      <DimtextOffsetInput
        label={getPropName("Width", "width")}
        disabled={disabled || noShapePropUpdates || disabledProps.width}
        offsetDisabled={disabled}
        labelWidth="5.5rem"
        value={item.width}
        offsetValue={item.width_offset}
        totalValue={item.cache.width_prime}
        {settings}
        allowNull
        on:input={updateItemWidthOffset} />
    {/if}
    {#if settings.hide_height_offset}
      <DimtextInput
        label={getPropName("Height", "height")}
        disabled={disabled || noShapePropUpdates || disabledProps.height}
        labelWidth="5.5rem"
        value={item.height}
        {settings}
        allowNull
        on:input={(e) => updateItemHeight(e.detail.value)} />
    {:else}
      <DimtextOffsetInput
        label={getPropName("Height", "height")}
        disabled={disabled || noShapePropUpdates || disabledProps.height}
        offsetDisabled={disabled}
        labelWidth="5.5rem"
        value={item.height}
        offsetValue={item.height_offset}
        totalValue={item.cache.height_prime}
        {settings}
        allowNull
        on:input={updateItemHeightOffset} />
    {/if}
  {/if}
  {#if !isCollection && UX_MODE !== "shpx"}
    <SelectInput
      label={getPropName("Type", "type_id")}
      disabled={disabled || disabledProps.type_id || noTypeUpdates}
      labelWidth="5.5rem"
      value={item.type_id}
      options={typeOptions}
      on:input={(e) => updateItem("type_id", e.detail.value)} />
    {#if $group.project_type !== "product"}
      <SelectInput
        label={getPropName("Opening", "collection_id")}
        disabled={disabled || disabledProps.collection_id}
        labelWidth="5.5rem"
        value={item.collection_id}
        options={collectionOptions}
        on:input={(e) => updateItem("collection_id", e.detail.value)} />
    {/if}
  {/if}
  {#if !framex}
  <TextInput
    label={getPropName("Quantity", "quantity")}
    disabled={disabled || disabledProps.quantity}
    labelWidth="5.5rem"
    value={item.quantity}
    parser={parsePosInteger}
    on:input={(e) => updateItem("quantity", e.detail.value)} />
  <TextInput
    label={getPropName("Description", "description")}
    disabled={disabled || disabledProps.description}
    labelWidth="5.5rem"
    value={item.description}
    on:input={(e) => updateItem("description", e.detail.value)} />
  {#if isCollection && standardColumns?.x_subdivisions?.visible}
    <TextInput
      label={getPropName("X Subdivisions", "x_subdivisions")}
      disabled={disabled || disabledProps.quantity}
      labelWidth="5.5rem"
      value={item.x_subdivisions}
      parser={parsePosInteger}
      on:input={(e) => updateSubdivs("x_subdivisions", e.detail.value)} />
  {/if}
  {#if isCollection && standardColumns?.y_subdivisions?.visible}
    <TextInput
      label={getPropName("Y Subdivisions", "y_subdivisions")}
      disabled={disabled || disabledProps.quantity}
      labelWidth="5.5rem"
      value={item.y_subdivisions}
      parser={parsePosInteger}
      on:input={(e) => updateSubdivs("y_subdivisions", e.detail.value)} />
  {/if}
  <TextInput
    label={getPropName(displayUnit === "inches" ? "Est. Area (sf)" : "Est. Area (m²)", "area")}
    readonly
    {disabled}
    labelWidth="5.5rem"
    value={item.cache.area}
    formatter={areaFormatter} />
  {/if}
  {#if !isCollection}
    <TextInput
      label={getPropName("Est. Wt. (lbs)", "weight")}
      readonly
      {disabled}
      labelWidth="5.5rem"
      value={item.cache.weight}
      formatter={(w) => (typeof w === "number" ? w.toFixed(0) : "")} />
    {#if $group.project_type !== "product"}
      <BooleanInput
        label="Stamp"
        {disabled}
        labelWidth="5.5rem"
        value={!!item.data.fabrications?.bug}
        on:input={toggleBug} />
    {/if}
    <FabricationsInput
      label="Fabrications"
      {disabled}
      labelWidth="5.5rem"
      value={item.data.fabrications}
      on:input={(e) => updateItem("data.fabrications", e.detail.value)} />
  {/if}
  <FlagInput
    label="Flag"
    {disabled}
    labelWidth="5.5rem"
    value={item.approval_status}
    on:input={(e) => updateItem("approval_status", e.detail.value)} />
  {#if !framex}
  {#if $group.project_type !== "product"}
    {#key item}
      {#if isCollection}
        <ButtonInput
          label="Grid Lines"
          {disabled}
          labelWidth="5.5rem"
          value={item.data.reference_planes?.type}
          buttons={gridLineButtons}
          on:input={(e) => updateGridLines(e.detail.value)} />
      {:else}
        <ButtonInput
          label="Ref. Planes"
          {disabled}
          labelWidth="5.5rem"
          value={item.data.reference_planes?.type}
          buttons={refPlaneButtons}
          on:input={(e) => updateRefPlanes(e.detail.value)} />
      {/if}
    {/key}
  {/if}
  <ContenteditableInput
    label={getPropName("Notes", "notes")}
    disabled={disabled || disabledProps.notes}
    labelWidth="5.5rem"
    value={item.notes}
    on:input={(e) => updateItem("notes", e.detail.value)} />
  {#if UX_MODE !== "shpx"}
    <ButtonInput
      label="Convert to"
      labelWidth="5.5rem"
      value={true}
      buttons={[{ label: item.is_collection ? "Item" : "Opening" }]}
      on:input={confirmToggleItemOpening} />
  {/if}
  {#if item.is_imported === true && !disabled}
    <ButtonInput
      label="Imported Item"
      labelWidth="5.5rem"
      value={item.is_imported}
      buttons={[{ label: "Convert", value: "convert" }]}
      on:input={() => updateItem("is_imported", false)} />
  {/if}
  <TextInput
    label={getPropName("Scope", "scope")}
    disabled={disabled || disabledProps.scope}
    labelWidth="5.5rem"
    value={item.scope}
    on:input={(e) => updateItem("scope", e.detail.value)} />
  {/if}
</SidebarSection>

{#if !framex && item.data.reference_planes?.type === "grid" && !Array.isArray(item.id)}
  <SidebarSection>
    <h3 class="px-2 mb-2">Grid Lines</h3>
    <DimtextInputMultiple
      label="X-Axis"
      {disabled}
      labelWidth="5.5rem"
      values={item.data.reference_planes.vertical}
      {settings}
      preserveOverallLength
      min={0}
      allowNull
      on:input={(evt) => updateData(["reference_planes", "vertical"], evt.detail.value)} />
    <DimtextInputMultiple
      label="Y-Axis"
      {disabled}
      labelWidth="5.5rem"
      values={item.data.reference_planes.horizontal}
      {settings}
      preserveOverallLength
      reversed
      min={0}
      allowNull
      on:input={(evt) => updateData(["reference_planes", "horizontal"], evt.detail.value)} />
  </SidebarSection>
{/if}

{#if !framex && customColumns.length > 0}
  <SidebarSection>
    <h3 class="px-2 mb-2">Custom Properties</h3>
    {#each customColumns as col}
      <TextInput
        label={col.name}
        {disabled}
        labelWidth="5.5rem"
        value={get(item, `custom_column_data.${col.id}`)}
        on:input={(evt) => updateCustomColData(col.id, evt.detail.value)} />
    {/each}
  </SidebarSection>
{/if}

{#if !isCollection && $group.project_type !== "product"}
  <SidebarSection>
    <h3 class="px-2 mb-2">Edge Treatment</h3>
    <div class="flex items-center pr-1">
      <SelectInput
        label="All edges"
        labelWidth="5.5rem"
        value={allTreatments}
        disabled={showIndividualEdges || disabled}
        on:mouseover={() => dispatch("mouseoverEdges", edgeIndices)}
        on:mouseout={() => dispatch("mouseoutEdges")}
        options={$edgeTreatments}
        on:input={(evt) => updateAllEdges(evt.detail.value)} />
      <button
        class="p-1 flex-none rounded hover:bg-gray-100"
        class:bg-gray-100={showIndividualEdges}
        on:click={() => (showIndividualEdges = !showIndividualEdges)}>
        <EllipsisIcon />
      </button>
    </div>
    {#if showIndividualEdges}
      {#each edgeIndices as i (i)}
        <SelectInput
          label={`Edge ${i + 1}`}
          labelWidth="5.5rem"
          value={treatment?.[i]}
          {disabled}
          on:mouseover={() => dispatch("mouseoverEdges", [i])}
          on:mouseout={() => dispatch("mouseoutEdges")}
          options={$edgeTreatments}
          on:input={(evt) => updateData(["fabrications", "edge_treatment", i], evt.detail.value)} />
      {/each}
    {/if}
  </SidebarSection>
{/if}

<Modal
  bind:this={convertModal}
  closeable
  on:confirm={toggleItemOpening}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Convert", type: "confirm", style: "danger" },
  ]}>
  <div slot="title">Convert {item.is_collection ? "Opening" : "Item"}</div>
  <div slot="content" class="space-y-4">
    <div>
      Are you sure you want to convert this {item.is_collection ? "opening" : "item"}
      to an {item.is_collection ? "item" : "opening"}?
    </div>
  </div>
</Modal>
<CustomScaleModal bind:this={customScaleModal} on:setScale={updateClipScale} />
<LlmRequestInfo bind:this={llmRequestModal} />

<style lang="scss">
  .mini-editor {
    height: 30rem;
    position: relative;
  }

  .image-action-button {
    display: none;
  }

  .image-container:hover {
    .image-action-button {
      display: block;
    }
  }
</style>
